"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { motion } from "framer-motion"
import { Home, Search, ReceiptText, ShoppingBag, User } from "lucide-react"
import { useApp } from "@/lib/context"
import GlassSearch from "./glass-search"

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
}

/**
 * Barra de navegação flutuante em glassmorfismo — todos os temas.
 * Agora · Pesquisar · Bufete (quando existe) · Meus Pedidos · Pedido (badge)
 */
export default function FloatingNav() {
  const router = useRouter()
  const { isValidSession, draftCount, buffete, currentOrderId, liveNotification, customerAuth } =
    useApp()
  const [showSearch, setShowSearch] = useState(false)

  const irParaAgora = () => {
    // Com pedido em curso → acompanhamento ao vivo; sem pedido → topo do menu
    if (currentOrderId) router.push("/agora")
    else scrollToId("hero")
  }

  if (!isValidSession) return null

  return (
    <>
      <motion.nav
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring", damping: 22, stiffness: 220 }}
        aria-label="Navegação do menu"
        className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-3 safe-bottom"
      >
        {/* Compacta e rolável em ecrãs estreitos; nunca transborda */}
        <div className="glass-strong liquid-sheen flex max-w-full items-center gap-0 overflow-x-auto rounded-full p-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-1">
          <NavButton icon={Home} label="Agora" highlight={!!currentOrderId} dot={!!liveNotification} onClick={irParaAgora} />
          <NavButton icon={Search} label="Buscar" onClick={() => setShowSearch(true)} hideLabelBelow="sm" />

          {buffete && (
            <NavButton icon={ShoppingBag} label="Bufete" highlight hideLabelBelow="sm" onClick={() => scrollToId("sec-bufete")} />
          )}

          <NavButton
            icon={ReceiptText}
            label="Pedidos"
            hideLabelBelow="sm"
            onClick={() => router.push("/pedidos")}
          />

          {/* Conta do cliente — só aparece com sessão iniciada; chama-o pelo nome */}
          {customerAuth?.name && (
            <NavButton
              icon={User}
              label={customerAuth.name.split(" ")[0]}
              onClick={() => router.push("/conta")}
            />
          )}

          {/* CTA do pedido → página completa */}
          <button
            onClick={() => router.push("/pedido")}
            className="relative ml-1 flex items-center gap-2 rounded-full px-4 py-2.5 text-primary-foreground shadow-lg transition-transform hover:scale-[1.03] active:scale-95 sm:px-5"
            style={{ background: "linear-gradient(120deg, hsl(var(--primary)), hsl(var(--primary) / 0.85))" }}
            aria-label={`Abrir pedido${draftCount > 0 ? ` (${draftCount} itens)` : ""}`}
          >
            <ShoppingBag className="size-[18px]" aria-hidden="true" />
            <span className="hidden text-xs font-semibold sm:inline">Pedido</span>
            {draftCount > 0 && (
              <motion.span
                key={draftCount}
                initial={{ scale: 0.4 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 18 }}
                className="flex size-5 items-center justify-center rounded-full bg-white text-[0.65rem] font-bold text-primary"
              >
                {draftCount > 9 ? "9+" : draftCount}
              </motion.span>
            )}
          </button>
        </div>
      </motion.nav>

      <GlassSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
    </>
  )
}

function NavButton({
  icon: Icon,
  label,
  onClick,
  highlight = false,
  dot = false,
  hideLabelBelow,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  onClick: () => void
  highlight?: boolean
  dot?: boolean
  /** Esconde a etiqueta em ecrãs pequenos para a barra caber sempre. */
  hideLabelBelow?: "sm"
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`relative flex shrink-0 flex-col items-center gap-0.5 rounded-full px-2.5 py-2 transition-colors hover:bg-white/40 sm:px-3 ${
        highlight ? "text-primary" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <span className="relative">
        <Icon className="size-[18px]" aria-hidden="true" />
        {dot && (
          <>
            <motion.span
              key={label + "-ping"}
              initial={{ scale: 0 }}
              animate={{ scale: [1, 1.6, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              className="absolute -right-1 -top-0.5 size-2 rounded-full bg-primary/50"
              aria-hidden="true"
            />
            <span className="absolute -right-1 -top-0.5 size-2 rounded-full bg-primary ring-2 ring-background" aria-hidden="true" />
          </>
        )}
      </span>
      <span
        className={`text-[0.55rem] font-semibold uppercase tracking-wider ${
          hideLabelBelow === "sm" ? "hidden sm:inline" : ""
        } max-w-[64px] truncate`}
      >
        {label}
      </span>
    </button>
  )
}
